/**
 * FilterBar — multi-select facet chips with optional clear control.
 *
 * Single responsibility: render facet chips and emit toggle events. State
 * lives in the caller (typically `useFacetSelection`).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";

export interface FilterFacet<T extends string> {
  /** Unique facet id. */
  readonly id: T;
  /** Display label. */
  readonly label: string;
  /** Optional count badge appended to the label. */
  readonly count?: number;
  /** Optional accent color when active. */
  readonly color?: string;
}

export interface FilterBarProps<T extends string> {
  /** Available facets. */
  readonly facets: readonly FilterFacet<T>[];
  /** Currently active facet ids. */
  readonly active: ReadonlySet<T>;
  /** Toggle handler. */
  readonly onToggle: (id: T) => void;
  /** Optional clear-all handler; rendered as a "Clear" button when present and any facet is active. */
  readonly onClear?: () => void;
  /** Optional left-side label for the bar. */
  readonly label?: string;
}

/**
 * Renders a horizontal multi-select facet chip group.
 *
 * Uses a generic parameter `T extends string` so callers can constrain the
 * facet id space to a literal union (e.g. NeutralizationMode).
 *
 * Wrapped in `React.memo` (via the generic-preserving `genericMemo` cast
 * below) so the bar does not rerender when only unrelated parent state
 * changes — facet sets are typically stable references.
 */
function FilterBarImpl<T extends string>({
  facets,
  active,
  onToggle,
  onClear,
  label,
}: FilterBarProps<T>): React.JSX.Element {
  const hasActive = active.size > 0;

  return (
    <div
      role="group"
      aria-label={label ?? "Filters"}
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "0.375rem",
      }}
    >
      {label !== undefined && (
        <span
          style={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: GRAY[600],
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginRight: "0.25rem",
          }}
        >
          {label}
        </span>
      )}
      {facets.map((facet) => {
        const isActive = active.has(facet.id);
        const accent = facet.color ?? PRIMARY[600];
        return (
          <button
            key={facet.id}
            type="button"
            aria-pressed={isActive}
            onClick={(): void => {
              onToggle(facet.id);
            }}
            style={{
              padding: "0.25rem 0.625rem",
              borderRadius: "9999px",
              border: `1px solid ${isActive ? accent : GRAY[300]}`,
              background: isActive ? accent : "white",
              color: isActive ? "white" : GRAY[700],
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.375rem",
            }}
          >
            <span>{facet.label}</span>
            {facet.count !== undefined && (
              <span
                style={{
                  fontSize: "0.6875rem",
                  padding: "0.05rem 0.35rem",
                  borderRadius: "9999px",
                  backgroundColor: isActive ? "rgba(255,255,255,0.25)" : GRAY[100],
                  color: isActive ? "white" : GRAY[600],
                  fontWeight: 600,
                }}
              >
                {facet.count}
              </span>
            )}
          </button>
        );
      })}
      {onClear !== undefined && hasActive && (
        <button
          type="button"
          onClick={onClear}
          style={{
            padding: "0.25rem 0.5rem",
            borderRadius: "0.25rem",
            border: "none",
            background: "transparent",
            color: GRAY[600],
            fontSize: "0.75rem",
            fontWeight: 500,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

/**
 * `React.memo` strips generics from the wrapped component. We restore the
 * generic signature with a single typed cast — runtime behavior is the
 * memoized component; the type is the original generic function.
 */
export const FilterBar = React.memo(FilterBarImpl) as <T extends string>(
  props: FilterBarProps<T>,
) => React.JSX.Element;
